import React, { FC } from 'react';
import { Field, FieldProps } from 'formik';
import { HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';

interface InputProps {
  name: string;
  placeholder?: string;
  type?: string;
  label?: string;
  disabled?: boolean;
  rows?: number;
  customClass?: string;
  note?: string;
  validate?: any;
  handlePasswordVisibility?: (name: string) => void;
  showEye?: boolean;
  [x: string]: any;
}

interface OptionProps {
  value: string;
  label: string;
}

export const BaseInput: FC<InputProps> = ({
  field,
  type,
  values,
  name,
  setFieldValue,
  placeholder,
  options,
  disabled,
  customOnChange,
  rows,
  className,
  handlePasswordVisibility,
  showEye,
  value,
  onChange,
  ...props
}) => {
  // Determine current value: prioritize direct prop, then Formik value, then empty string
  const currentValue = value !== undefined ? value : (values && values[name]) || '';

  // Handle change: prioritize custom/direct onChange, then Formik's
  const handleChange = (e: any) => {
    if (onChange) onChange(e);
    if (customOnChange) customOnChange(e);
    if (setFieldValue && name) setFieldValue(name, e.target.value);
  };

  const baseClasses = `
    w-full
    bg-gray-50 dark:bg-gray-800 
    border border-gray-200 dark:border-gray-700 
    rounded-xl 
    text-gray-900 dark:text-white
    placeholder:text-gray-400
    transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 hover:border-gray-300 dark:hover:border-gray-600
    disabled:bg-gray-100 dark:disabled:bg-gray-900 disabled:cursor-not-allowed disabled:shadow-none
    ${type === 'date' ? 'accent-primary-500 min-h-[48px]' : ''}
  `;

  switch (type) {
    case 'textarea':
      return (
        <textarea
          disabled={disabled}
          placeholder={placeholder}
          value={currentValue}
          onChange={handleChange}
          rows={rows || 4}
          {...props}
          className={`${baseClasses} ${className || ''} min-h-[100px] resize-y p-4 active:border-primary-500`}
        />
      );

    case 'checkbox':
      return (
        <label className='flex items-center space-x-3 cursor-pointer group'>
          <input
            {...props}
            type='checkbox'
            disabled={disabled}
            checked={!!currentValue}
            onChange={handleChange}
            className='
              w-5 h-5 
              text-primary-600 
              border-gray-300 dark:border-gray-600 
              rounded 
              focus:ring-primary-500 
              dark:bg-gray-700 
              cursor-pointer
              disabled:opacity-50 disabled:cursor-not-allowed
            '
          />
          {props.checkboxLabel && (
            <span className='text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors'>
              {props.checkboxLabel}
            </span>
          )}
        </label>
      );

    case 'select':
      return (
        <div className='relative'>
          <select
            disabled={disabled}
            value={currentValue}
            onChange={handleChange}
            className={`${baseClasses} appearance-none cursor-pointer h-12 px-4 ${className || ''}`}
            {...props}
          >
            {options?.map((option: OptionProps) => (
              <option key={option.value} value={option.value} className='bg-white dark:bg-gray-800'>
                {option.label}
              </option>
            ))}
          </select>
          <div className='absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 dark:text-gray-400'>
             <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' className='w-4 h-4'>
                <path d='m6 9 6 6 6-6'/>
             </svg>
          </div>
        </div>
      );

    default:
      return (
        <div className='relative'>
          <input
            {...props}
            type={type}
            disabled={disabled}
            placeholder={placeholder}
            value={currentValue}
            onChange={handleChange}
            className={`${baseClasses} ${className || ''} h-12 px-4 ${showEye ? 'pr-12' : ''} ${type === 'date' ? 'date-input-modern pl-11' : ''}`}
          />
          {type === 'date' && (
             <div className='absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none'>
                <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' className='w-5 h-5'>
                    <rect x='3' y='4' width='18' height='18' rx='2' ry='2' />
                    <line x1='16' y1='2' x2='16' y2='6' />
                    <line x1='8' y1='2' x2='8' y2='6' />
                    <line x1='3' y1='10' x2='21' y2='10' />
                </svg>
             </div>
          )}
          {showEye && (
            <button
              type='button'
              onClick={() => handlePasswordVisibility?.(name)}
              className='absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg transition-colors'
            >
              {type === 'password' ? <HiOutlineEye className='w-5 h-5' /> : <HiOutlineEyeOff className='w-5 h-5' />}
            </button>
          )}
        </div>
      );
  }
};

const Input: FC<InputProps> = ({
  name,
  placeholder,
  type,
  label,
  disabled,
  rows,
  customClass,
  note,
  validate,
  showEye,
  handlePasswordVisibility,
  ...props
}) => (
  <Field name={name} validate={validate}>
    {({ field, meta, form: { values, setFieldValue } }: FieldProps) => {
      const isError = (meta.touched && meta.error) || props.isError;
      
      return (
        <div className='flex flex-col w-full space-y-1.5'>
          {label && (
            <label 
              htmlFor={name} 
              className='block text-sm font-medium text-gray-700 dark:text-gray-200'
            >
              {label}
              {note && (
                <span className='ml-1 text-xs text-gray-500 dark:text-gray-400 font-normal'>
                  ({note})
                </span>
              )}
            </label>
          )}

          <div className='relative'>
            <BaseInput
              type={type}
              field={field}
              values={values}
              name={name}
              setFieldValue={setFieldValue}
              placeholder={placeholder}
              disabled={disabled}
              showEye={showEye}
              handlePasswordVisibility={handlePasswordVisibility}
              rows={rows}
              {...props}
              className={`
                ${customClass ?? ''}
                ${isError ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}
              `}
            />
          </div>

          {meta.touched && meta.error && (
            <p className='text-sm text-red-500 mt-1 animate-in slide-in-from-top-1'>
              {meta.error}
            </p>
          )}
        </div>
      );
    }}
  </Field>
);

export default Input;
